import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdInput, CmdLabel, CmdLoader, CmdSimpleSelect } from '@commander-services/gui-components';
import { Controller, FieldError, get, useFieldArray, useForm } from 'react-hook-form';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AreaTypes,
  FUEL_AREA_TYPE,
  IAddWaypointRequestData,
  IDetailWaypointResponseData,
  IFormWaypointResponseData,
  IVerifyDeleteRequestData,
  IWaypointPostion,
  translateOptions,
} from './types';
import {
  convertCoordinatesToArray,
  getAvailableFuelOptions,
  getWaypointFormData,
  handleAreaTypeValue,
  updateWaypoint,
  verifyRemoveWaypoint,
} from './WaypointService';
import ConfirmDialog from '../../ConfirmDialog';
import SimpleSelectWithIcon from '../../Forms/ReactHookFormFields/SimpleSelectWithIcon';
import { WAYPOINT_QUERY_KEY } from './constants';
import { transformErrors, validateFields } from '../../../Services/HookFormService';
import { selectedCustomersAtom } from '../../../store/recoil/customers';
import {
  activeFilterAtom,
  centerWaypointAtom,
  computedAreaAtom,
  coordinatesAtom,
  detailWaypointAtom,
  isCopyModeWpAtom,
  isEditModeAtom,
  showWaypointLoaderAtom,
  waypointDataAtom,
  waypointIdAtom,
} from './WaypointState';
import { STATUS_WARNING } from '../../../Services/HttpService';

interface IEditWaypointForm {
  closeEditWaypoint: (event?: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
  saveEditWaypoint: () => void;
}

interface EditWaypointFormValues {
  icon: string;
  name: string;
  type: string;
  customer: string;
  areaType: AreaTypes | '';
  refuelingTypeList: string;
  fuels?: {
    fuelTypeId: string;
    unitPriceIncVat: string;
  }[];
}
enum ConfirmDialogType {
  Exit = 'exit',
  AreaTypeVerify = 'areaTypeVerify',
}

function EditWaypointForm(props: IEditWaypointForm): JSX.Element {
  const intlShape = useIntl();
  const { formatMessage: f } = intlShape;
  const computedArea = useRecoilState<number>(computedAreaAtom)[0];
  const [_detailWaypoint, setDetailWaypoint] = useRecoilState<IDetailWaypointResponseData | null>(
    detailWaypointAtom
  );
  const isCopyModeWp = useRecoilValue<boolean>(isCopyModeWpAtom);
  const [formOptions, setFormOptions] = React.useState<IFormWaypointResponseData | null>(null);
  const [customerLabel, setCustomerLabel] = React.useState<string>('');
  const [isOpenConfirmDialog, setIsOpenConfirmDialog] = React.useState(false);
  const coordinates = useRecoilValue<IWaypointPostion[] | null>(coordinatesAtom);
  const setIsEditMode = useSetRecoilState<boolean>(isEditModeAtom);
  const center = useRecoilValue<IWaypointPostion | null>(centerWaypointAtom);
  const [waypointData, setWaypointData] = useRecoilState<IDetailWaypointResponseData | null>(
    waypointDataAtom
  );
  const waypointId = useRecoilValue<number | null>(waypointIdAtom);
  const setShowWaypointLoader = useSetRecoilState(showWaypointLoaderAtom);
  const queryClient = useQueryClient();
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const activeFilter = useRecoilValue(activeFilterAtom);
  const [confirmDialogType, setConfirmDialogType] = React.useState<ConfirmDialogType>(
    ConfirmDialogType.Exit
  );
  const [verifyDeleteData, setVerifyDeleteData] = React.useState<IVerifyDeleteRequestData | null>(
    null
  );
  const [waypointReferenceTitle, setWaypointReferenceTitle] = React.useState<string[]>([]);
  const [pendingData, setPendingData] = React.useState<IAddWaypointRequestData | null>(null);
  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    setError,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<EditWaypointFormValues>({
    mode: 'onChange',
    defaultValues: {
      icon:
        waypointData && waypointData.category && waypointData.category.id
          ? String(waypointData.category.id)
          : '',
      name: waypointData && waypointData.name ? waypointData.name : '',
      type: waypointData?.type ? waypointData.type : '',
      customer: waypointData && waypointData.customerId ? String(waypointData.customerId) : '',
      areaType: waypointData ? handleAreaTypeValue(waypointData).value : '',
      refuelingTypeList: waypointData?.refueling ? waypointData.refueling.type : '',
      fuels: [
        {
          fuelTypeId: '',
          unitPriceIncVat: '',
        },
      ],
    },
    resolver: async (values: EditWaypointFormValues) => {
      const formValidation = {
        icon: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ],
        name: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
          {
            param: 'maxLength',
            value: 100,
            message: f({ id: 'form.maxLength' }, { value: 100 }),
          },
        ],
        areaType: [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ],
      } as Record<string, { param: string; value: boolean; message: string }[]>;

      if (values.areaType && values.areaType !== 'waypoint') {
        formValidation.refuelingTypeList = [
          {
            param: 'required',
            value: true,
            message: f({ id: 'form.required' }),
          },
        ];
      }

      const validateErrors = validateFields(values, formValidation);

      const formErrors: Record<string, FieldError> = {};

      if (values.areaType && values.areaType !== 'waypoint' && Array.isArray(values.fuels)) {
        values.fuels.forEach((fuel, index) => {
          if (!fuel.fuelTypeId) {
            formErrors[`fuels.${index}.fuelTypeId`] = {
              type: 'required',
              message: f({ id: 'form.required' }),
            };
          }

          if (
            fuel.unitPriceIncVat === '' ||
            fuel.unitPriceIncVat === null ||
            fuel.unitPriceIncVat === undefined
          ) {
            formErrors[`fuels.${index}.unitPriceIncVat`] = {
              type: 'required',
              message: f({ id: 'form.required' }),
            };
          } else {
            const value = parseFloat(fuel.unitPriceIncVat);
            if (Number.isNaN(value) || value < 0) {
              formErrors[`fuels.${index}.unitPriceIncVat`] = {
                type: 'min',
                message: f({ id: 'form.rules.minValue' }, { value: 0 }),
              };
            } else if (value > 999999.99) {
              formErrors[`fuels.${index}.unitPriceIncVat`] = {
                type: 'max',
                message: f({ id: 'form.rules.maxValue' }, { value: 999999.99 }),
              };
            }
          }
        });
      }

      const finalErrors = {
        ...transformErrors(validateErrors),
        ...formErrors,
      };

      return {
        values: Object.keys(finalErrors).length ? {} : values,
        errors: finalErrors,
      };
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    name: 'fuels',
    control,
    // rules: { maxLength: 4 },
  });

  const handleFormOptions = async () => {
    if (!isCopyModeWp) {
      setShowWaypointLoader(true);
      const response = await getWaypointFormData();

      if (response && waypointData) {
        setFormOptions(response);
        const label = response.customerId.values.find(
          (item) => String(item.value) === String(waypointData.customerId)
        );
        setCustomerLabel(`${label?.item} (${label?.label})`);
        setShowWaypointLoader(false);
      }
    }

    if (waypointData && waypointData.refueling) {
      waypointData.refueling?.fuels.map((item, index) => {
        update(index, {
          fuelTypeId: String(item.fuelTypeId),
          unitPriceIncVat: String(item.unitPriceIncVat),
        });
      });
    }
    setShowWaypointLoader(false);
  };

  const { isLoading } = useQuery({
    queryKey: ['editWaypointFormOptions', waypointData, isCopyModeWp],
    queryFn: () => handleFormOptions(),
    // staleTime: 9000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const updateWaypoints = () => {
    queryClient.invalidateQueries({
      queryKey: [WAYPOINT_QUERY_KEY, selectedCustomers, activeFilter],
    });
  };

  const handleSubmitEditWaypoint = async (data: IAddWaypointRequestData) => {
    if (!waypointId) return;

    const response = await updateWaypoint(waypointId, data);

    if (response?.status === STATUS_WARNING) {
      const message = response?.toast?.message;
      if (message) {
        setError('name', {
          type: 'required',
          message: f({ id: `${message}` }),
        });
      }
      return;
    }

    setIsEditMode(false);
    props.saveEditWaypoint();
    updateWaypoints();
    setDetailWaypoint(null);
    setWaypointData(null);
  };

  const handleOnSubmit = async (values: EditWaypointFormValues) => {
    if (Object.keys(errors).length > 0) return;
    const trimName: string = values.name.trim();

    if (waypointData && coordinates && center && waypointId) {
      const baseData: IAddWaypointRequestData = {
        name: trimName,
        type: values.type ? values.type : waypointData.type,
        centerPoint: center,
        customerId: Number(values.customer),
        area: computedArea,
        categoryId: Number(values.icon),
        coordinates: convertCoordinatesToArray(coordinates, true),
        waypoint: values.areaType !== FUEL_AREA_TYPE,
      };

      let data: IAddWaypointRequestData = baseData;

      if (
        values.areaType !== 'waypoint' &&
        values.areaType &&
        typeof values.refuelingTypeList === 'string' &&
        Array.isArray(values.fuels) &&
        values.fuels.length > 0
      ) {
        data = {
          ...baseData,
          refueling: {
            type: values.refuelingTypeList,
            fuels: values.fuels.map((fuel) => ({
              fuelTypeId: Number(fuel.fuelTypeId),
              unitPriceIncVat: Number(fuel.unitPriceIncVat),
            })),
          },
        };
      }

      if (waypointId && waypointData?.waypoint && values.areaType === FUEL_AREA_TYPE) {
        const responseVerify = await verifyRemoveWaypoint(waypointId);
        if (responseVerify && responseVerify?.details?.length > 0) {
          const findType = responseVerify.details.find((item) => item.type === 'alarm');

          if (findType) {
            const tempTitle = findType.details.map((item) => item.name);

            setWaypointReferenceTitle(tempTitle);
          }

          setVerifyDeleteData(responseVerify);
          setPendingData(data);
          setConfirmDialogType(ConfirmDialogType.AreaTypeVerify);
          setIsOpenConfirmDialog(true);
          return;
        }
      }

      const response = await updateWaypoint(waypointId, data);

      if (response && response.status === STATUS_WARNING) {
        const message = response?.toast?.message;

        if (message) {
          setError('name', {
            type: 'required',
            message: f({ id: `${message}` }),
          });
        }
        return;
      }

      if (response) {
        setIsEditMode(false);
        props.saveEditWaypoint();
        updateWaypoints();
        setDetailWaypoint(null);
        setWaypointData(null);
      }
    } else if (waypointData && waypointData.coordinates && waypointId) {
      const baseData: IAddWaypointRequestData = {
        name: trimName,
        type: values.type ? values.type : waypointData.type,
        centerPoint: waypointData.centerPoint,
        customerId: Number(values.customer),
        area: computedArea,
        categoryId: Number(values.icon),
        coordinates: convertCoordinatesToArray(waypointData.coordinates, true),
        waypoint: values.areaType !== FUEL_AREA_TYPE,
      };

      let data: IAddWaypointRequestData = baseData;

      if (
        values.areaType !== 'waypoint' &&
        values.areaType &&
        typeof values.refuelingTypeList === 'string' &&
        Array.isArray(values.fuels) &&
        values.fuels.length > 0
      ) {
        data = {
          ...baseData,
          refueling: {
            type: values.refuelingTypeList,
            fuels: values.fuels.map((fuel) => ({
              fuelTypeId: Number(fuel.fuelTypeId),
              unitPriceIncVat: Number(fuel.unitPriceIncVat),
            })),
          },
        };
      }

      if (waypointId && waypointData?.waypoint && values.areaType === FUEL_AREA_TYPE) {
        const responseVerify = await verifyRemoveWaypoint(waypointId);
        if (responseVerify && responseVerify?.details?.length > 0) {
          const findType = responseVerify.details.find((item) => item.type === 'alarm');

          if (findType) {
            const tempTitle = findType.details.map((item) => item.name);

            setWaypointReferenceTitle(tempTitle);
          }

          setVerifyDeleteData(responseVerify);
          setPendingData(data);
          setConfirmDialogType(ConfirmDialogType.AreaTypeVerify);
          setIsOpenConfirmDialog(true);
          return;
        }
      }

      const response = await updateWaypoint(waypointId, data);

      if (response && response.status === STATUS_WARNING) {
        const message = response?.toast?.message;

        if (message) {
          setError('name', {
            type: 'required',
            message: f({ id: `${message}` }),
          });
        }
        return;
      }

      if (response) {
        setIsEditMode(false);
        props.saveEditWaypoint();
        updateWaypoints();
        setDetailWaypoint(null);
        setWaypointData(null);
      }
    }
  };

  const handleCloseConfirmDialog = () => {
    setIsOpenConfirmDialog(false);
  };

  const handleContinueConfirmDialog = () => {
    if (confirmDialogType === ConfirmDialogType.Exit) {
      setIsOpenConfirmDialog(false);
      props.closeEditWaypoint();
    } else if (confirmDialogType === ConfirmDialogType.AreaTypeVerify && pendingData) {
      setIsOpenConfirmDialog(false);
      handleSubmitEditWaypoint(pendingData);
    }
  };
  const handleConfirmDialog = () => {
    setConfirmDialogType(ConfirmDialogType.Exit);
    setIsOpenConfirmDialog(true);
  };

  const handleAddFuel = () => {
    append({
      fuelTypeId: '',
      unitPriceIncVat: '',
    });
  };

  const handleRemoveFuel = (index: number) => {
    remove(index);
  };

  const fuelIcon = () => {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
      >
        <g id="Icons/16px/add_fuel" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
          <path
            d="M10.6666667,0 C11.7695333,0 12.6666667,0.978690909 12.6666667,2.18181818 L12.665786,8.01102637 C11.41985,8.09349427 10.2970651,8.6336328 9.46534894,9.46534894 C8.55988567,10.3708122 8,11.62125 8,13 C8,14.1247937 8.37262679,15.1641904 9.0015212,16.0005527 L0.666666667,16 C0.2985,16 0,15.6743636 0,15.2727273 C0,14.8710909 0.2985,14.5454545 0.666666667,14.5454545 L1.33333333,14.5454545 L1.33333333,2.18181818 C1.33333333,0.978690909 2.23046667,0 3.33333333,0 Z M16,2.90909091 L16.0005527,9.0015212 C15.5986285,8.6992982 15.1498154,8.4562577 14.6671691,8.28531337 L14.6666667,4.36363636 L13.3333333,4.36363636 L13.3333333,2.90909091 L16,2.90909091 Z M10,2.8 L4,2.8 L4,7.2 L10,7.2 L10,2.8 Z"
            id="Combined-Shape"
            fill="#737373"
          />
          <path
            d="M13,10 C11.3431458,10 10,11.3431458 10,13 C10,14.6568542 11.3431458,16 13,16 C14.6568542,16 16,14.6568542 16,13 C16,12.2043505 15.6839295,11.4412888 15.1213203,10.8786797 C14.5587112,10.3160705 13.7956495,10 13,10 Z M14.5,13.5 L13.5,13.5 L13.5,14.5 L12.5,14.5 L12.5,13.5 L11.5,13.5 L11.5,12.5 L12.5,12.5 L12.5,11.5 L13.5,11.5 L13.5,12.5 L14.5,12.5 L14.5,13.5 Z"
            id="Shape"
            fill="#737373"
            fillRule="nonzero"
          />
        </g>
      </svg>
    );
  };

  const allFuels = watch('fuels') ?? [];

  const filledFuelIds = allFuels.map((fuel) => fuel.fuelTypeId).filter(Boolean);

  const maxFuelTypes = formOptions?.fuelTypeList?.length ?? 0;

  const canAddMoreFuels =
    watch('areaType') !== 'waypoint' &&
    !!watch('areaType') &&
    filledFuelIds.length < maxFuelTypes &&
    fields.length < maxFuelTypes;

  return (
    <form onSubmit={handleSubmit(handleOnSubmit)}>
      {isLoading && <CmdLoader inContent={true} />}

      <div className="b-mc-waypoints">
        <div className="b-mc-waypoints-title">
          <a href="#" className="flex items-center">
            <i className="cmd-icon cmd-icon-back mr-2" onClick={handleConfirmDialog} />
            {f({ id: 'enum.area.edit.record' })}
          </a>
        </div>
        <div className="b-mc-waypoints-content h-[300px] overflow-y-auto">
          {isOpenConfirmDialog && confirmDialogType === ConfirmDialogType.Exit && (
            <ConfirmDialog
              title={f({ id: 'waypoint.add.confirm.dialog.title' })}
              cancelCallback={handleCloseConfirmDialog}
              continueCallback={handleContinueConfirmDialog}
              cancelButtonTitle={f({ id: 'waypoint.add.confirm.dialog.cancel' })}
              continueButtonTitle={f({ id: 'waypoint.add.confirm.dialog.confirm' })}
            >
              {f({ id: 'waypoint.add.confirm.dialog.message' })}
            </ConfirmDialog>
          )}

          {isOpenConfirmDialog &&
            confirmDialogType === ConfirmDialogType.AreaTypeVerify &&
            waypointData?.name &&
            verifyDeleteData &&
            verifyDeleteData?.details?.length > 0 && (
              <ConfirmDialog
                title={f({ id: 'waypoint.confirm.dialog.title' }, { name: waypointData.name })}
                cancelCallback={handleCloseConfirmDialog}
                continueCallback={handleContinueConfirmDialog}
                cancelButtonTitle={f({ id: 'waypoint.confirm.dialog.cancel' })}
                continueButtonTitle={f({ id: 'waypoints.edit.save' })}
                tooltip={waypointReferenceTitle.join('\n')}
              >
                {verifyDeleteData?.details?.length > 0 &&
                  `${f({ id: 'enum.area.edit.dependency.message' })} (${verifyDeleteData.references}):
                   ${waypointReferenceTitle.join(',\n')}. ${f({ id: 'enum.area.edit.dependency.message.question' })}`}
              </ConfirmDialog>
            )}

          <Controller
            name="areaType"
            control={control}
            render={({ field }) => (
              <CmdSimpleSelect
                {...field}
                id="areaType"
                // name="customer"
                label={f({ id: 'enum.area.tab.title' })}
                // labelClassName="col-3 form-label"
                introOption={f({ id: 'form.choose' })}
                options={translateOptions(intlShape)}
                // rowClassName="row align-items-center"
                // columnClassName="col-9"
                searchPlaceholder={f({ id: 'waypoints.form.customer.search' })}
                required={true}
                onChangeCallback={async (value) => {
                  setValue('areaType', value);
                  trigger('areaType');

                  if (value === 'waypoint') {
                    clearErrors('fuels');
                    trigger();
                  }
                }}
              />
            )}
          />
          {formOptions &&
            waypointData &&
            waypointData.customerId &&
            formOptions.customerId.values &&
            customerLabel && (
              <CmdLabel
                id="customer"
                title={f({ id: 'waypoints.form.customer.title' })}
                value={customerLabel}
              />
            )}

          {formOptions && formOptions.categoryId && (
            <Controller
              name="icon"
              control={control}
              render={({ field }) => (
                <SimpleSelectWithIcon
                  {...field}
                  // name="icon"
                  // id="icon"
                  label={f({ id: 'waypoints.form.category.title' })}
                  options={formOptions.categoryId.values}
                  introOption={f({ id: 'form.choose' })}
                  control={control}
                  required={true}
                />
              )}
            />
          )}

          {formOptions && (
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <CmdInput
                  {...field}
                  id="name"
                  type="text"
                  // labelClassName="col-3 form-label"
                  label={f({ id: 'waypoints.edit.title' })}
                  // rowClassName="row align-items-center"
                  // columnClassName="col-9"
                  placeholder={f({ id: 'waypoints.edit.title.required.placeholder' })}
                  onChangeCallback={(value) => {
                    setValue('name', value);
                    trigger('name');
                  }}
                  required={true}
                  error={errors.name?.message}
                  touched={!!errors.name}
                />
              )}
            />
          )}

          {formOptions && waypointData && (
            <CmdLabel
              id="type"
              title={f({ id: 'waypoints.edit.type' })}
              value={waypointData.typeName}
            />
          )}
          {watch('areaType') !== 'waypoint' &&
            watch('areaType') &&
            formOptions &&
            formOptions?.refuelingTypeList && (
              <>
                <p className="text-sm font-semibold mb-3">
                  {f({ id: 'enum.waypoints.area.type.fuel' })}
                </p>
                <div className="area-divider" />

                <div className="mt-4">
                  <Controller
                    name="refuelingTypeList"
                    control={control}
                    render={({ field }) => (
                      <CmdSimpleSelect
                        {...field}
                        id="refuelingTypeList"
                        label={f({ id: 'enum.area.refuelingTypeList.title' })}
                        introOption={f({ id: 'form.choose' })}
                        options={formOptions.refuelingTypeList || []}
                        searchPlaceholder={f({ id: 'waypoints.form.customer.search' })}
                        required={true}
                        onChangeCallback={(value) => {
                          setValue('refuelingTypeList', value);
                          trigger('refuelingTypeList');
                        }}
                        error={errors.refuelingTypeList?.message}
                        touched={!!errors.refuelingTypeList}
                      />
                    )}
                  />

                  {fields.map((item, index) => {
                    const fuels = watch('fuels') ?? [];
                    const currentValue = fuels[index]?.fuelTypeId ?? '';
                    const selectedFuelIds = fuels.map((fuel) => fuel.fuelTypeId).filter(Boolean);
                    const availableOptions = getAvailableFuelOptions(
                      formOptions.fuelTypeList,
                      selectedFuelIds,
                      currentValue
                    );

                    return (
                      <div key={item.id} className="mb-4">
                        {index > 0 && (
                          <div
                            className="flex justify-end items-center gap-2 mt-1 text-[#737373] cursor-pointer"
                            onClick={() => handleRemoveFuel(index)}
                          >
                            <i className="cmd-icon cmd-icon-bin" />
                            <span className="text-sm">{f({ id: 'table.deleteBtn' })}</span>
                          </div>
                        )}
                        <Controller
                          name={`fuels.${index}.fuelTypeId`}
                          control={control}
                          render={({ field }) => (
                            <CmdSimpleSelect
                              {...field}
                              id={`fuels.${index}.fuelTypeId`}
                              label={f({ id: 'table_list.columns.fuelTypeId' })}
                              introOption={f({ id: 'form.choose' })}
                              options={availableOptions}
                              searchPlaceholder={f({ id: 'waypoints.form.customer.search' })}
                              onChangeCallback={(value) => {
                                setValue(`fuels.${index}.fuelTypeId`, value);
                                // trigger(`fuels.${index}.fuelTypeId`);
                                trigger();
                              }}
                              required={true}
                              error={get(errors, `fuels.${index}.fuelTypeId`)?.message}
                              touched={get(errors, `fuels.${index}.fuelTypeId`)}
                            />
                          )}
                        />

                        <Controller
                          name={`fuels.${index}.unitPriceIncVat`}
                          control={control}
                          render={({ field }) => (
                            <CmdInput
                              {...field}
                              id={`fuels.${index}.unitPriceIncVat`}
                              type="number"
                              label={f({
                                id: 'enum.refuelingImportRecords.calculatedValues.unitPriceIncVat',
                              })}
                              placeholder={f({ id: 'form.enterUnitPriceIncVat' })}
                              onChangeCallback={(value) => {
                                setValue(`fuels.${index}.unitPriceIncVat`, value);
                                // trigger(`fuels.${index}.unitPriceIncVat`);
                                trigger();
                              }}
                              required={true}
                              error={get(errors, `fuels.${index}.unitPriceIncVat`)?.message}
                              touched={get(errors, `fuels.${index}.unitPriceIncVat`)}
                            />
                          )}
                        />
                        {index < fields.length - 1 && <div className="area-divider" />}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

          {canAddMoreFuels && (
            <div className="b-waypoints-list-actions mt-23">
              <a
                href="#"
                className="e-button-sq m-0"
                title={f({ id: 'enum.area.form.add.fuel' })}
                onClick={handleAddFuel}
                data-cy="area-add-fuel"
              >
                {fuelIcon()}
                <span className="sr-only">{f({ id: 'enum.area.form.add.fuel' })}</span>
              </a>
            </div>
          )}
        </div>
        <div className="b-mc-waypoints-footer">
          <div className="row">
            <div className="col">
              <a href="#" className="e-button" onClick={handleConfirmDialog}>
                {f({ id: 'waypoints.cancel' })}
              </a>
            </div>
            <div className="col">
              <button
                type="submit"
                className={`${Object.keys(errors).length > 0 ? 'e-button e-button--gray  e-button--disabled' : 'e-button e-button--gray'}`}
              >
                {f({ id: 'waypoints.edit.save' })}
              </button>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
}

export default EditWaypointForm;
